import { HTMLAttributes, useState } from 'react';
import { Box, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { makeStyles } from '@mui/styles';
import { useUser_GetPortalAccountUsersQuery } from 'queries';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

export interface UserAutoCompleteOption {
  id: string;
  name: string;
  avatarUrl: string;
}

type Props = {
  elementId: string;
  portalId: string;
  selectedUserId: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleUserChange: (elementId: string, e: any, option: UserAutoCompleteOption | null) => void;
  handleLoseFocus?: () => void;
  openByDefault?: boolean;
};

const useStyles = makeStyles(() => ({
  autocomplete: {
    width: '100%',
  },
  userImage: {
    marginRight: 10,
  },
}));

export default function PortalUsersAutocomplete({
  elementId,
  portalId,
  selectedUserId,
  handleUserChange,
  handleLoseFocus,
  openByDefault = false,
}: Props) {
  const classes = useStyles();
  const [open, setOpen] = useState(openByDefault);

  const { data: usersData, loading: usersLoading } = useUser_GetPortalAccountUsersQuery({
    variables: {
      input: {
        portalId,
        includeAccountDisabledUsers: false,
        includeClientUsers: false,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal account users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!usersData && usersLoading) {
    return <Spinner />;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (e: any, option: UserAutoCompleteOption | null) => {
    handleUserChange(elementId, e, option);
  };

  const userAcOptions = usersData!.data.map(
    (user) =>
      ({
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
        avatarUrl: user.avatarUrl,
      }) as UserAutoCompleteOption,
  );

  return (
    <Autocomplete
      id={elementId}
      className={classes.autocomplete}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={userAcOptions}
      value={selectedUserId ? userAcOptions.find((o) => o.id === selectedUserId) : null}
      getOptionLabel={(option: UserAutoCompleteOption) => option.name}
      renderOption={(props: HTMLAttributes<HTMLLIElement>, option: UserAutoCompleteOption) => (
        <Box component="li" {...props}>
          <img
            className={classes.userImage}
            loading="lazy"
            width="20"
            src={`${option.avatarUrl || '/default_avatar.svg'}`}
            srcSet={`${option.avatarUrl || '/default_avatar.svg'} 2x`}
            alt=""
          />
          {option.name}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search for a team member"
          size="small"
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
        />
      )}
      noOptionsText="No team members found"
      onChange={handleChange}
      onBlur={() => (handleLoseFocus ? handleLoseFocus() : undefined)}
    />
  );
}
